import React, { useEffect, useState } from 'react';
import BasicSelect from '../components/ApiPage/Dropdown';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ProjectPageHeader from '../components/ProjectPageHeader';
import projects from '../assets/projectsData';
import { getCurrentWeather } from '../Services/server-api';

const project = projects.find((p) => p.id === 'weather-api');
const API_COUNT_LIMIT = 5;

const progressSx = {
  height: 3,
  borderRadius: 2,
  backgroundColor: 'rgba(255,255,255,0.06)',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#ff8c00',
    transitionDuration: '8s',
  },
};

function ApiPage() {
  const [city, setCity] = useState('');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRawDataVisible, setIsRawDataVisible] = useState(false);
  const [cityCount, setCityCount] = useState(0);
  const [cityCountLimt, setCityCountLimit] = useState(false);
  const [cityHistory, setCityHistory] = useState([]);
  const [selectedButton, setSelectedButton] = useState(null);
  const fetchData = async (requestedCity) => {
    setIsLoading(true);
    setError('');

    try {
      const persisted = JSON.parse(localStorage.getItem('weather_cache') || 'null');
      const existing = persisted?.history?.find(
        (item) => item.name.toLowerCase() === requestedCity.toLowerCase()
      );
      if (existing) {
        setData(existing);
        setIsLoading(false);
        return;
      }

      const weather = await getCurrentWeather(requestedCity);
      const nextCount = cityCount + 1;
      const nextHistory = [...cityHistory, weather].slice(-API_COUNT_LIMIT);
      setData(weather);
      setCityCount(nextCount);
      setCityHistory(nextHistory);
      setCityCountLimit(nextCount >= API_COUNT_LIMIT);
      localStorage.setItem('weather_cache', JSON.stringify({ count: nextCount, history: nextHistory }));
    } catch (err) {
      setError(err.response?.data?.message || 'Weather lookup failed. Please try again.');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // load persisted weather cache (count and history)
    try {
      const persisted = JSON.parse(localStorage.getItem('weather_cache') || 'null');
      if (persisted) {
        setCityHistory(persisted.history || []);
        setCityCount(persisted.count || 0);
        if ((persisted.count || 0) >= API_COUNT_LIMIT) setCityCountLimit(true);
      }
    } catch (e) {}

    const requestedCity = city.trim();
    if (!requestedCity) {
      setIsLoading(false);
      setData(null);
      setError('');
      return undefined;
    }

    const historyMatch = cityHistory.find(
      (item) => item.name.toLowerCase() === requestedCity.toLowerCase()
    );
    if (historyMatch) {
      setData(historyMatch);
      setIsLoading(false);
      return undefined;
    }

    if (cityCountLimt) return undefined;
    setIsLoading(true);
    const timeout = window.setTimeout(() => fetchData(requestedCity), 500);
    return () => window.clearTimeout(timeout);
    // A lookup runs only when the typed city or limit changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, cityCountLimt]);

  const handleCityChange = (enteredCity) => {
    setCity(enteredCity);

    if (enteredCity.trim() === '') {
      setIsLoading(false);
      setData(null);
      setError('');
    } else {
      setIsLoading(true);
      setError('');
    }
  };

  const formatTemperature = (temperature) => `${temperature}°C`;

  const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], {
      timeStyle: 'short',
      timeZone: 'UTC',
    });
  };

  const handleListClick = (item) => {
    setCity(item.name);
    setIsLoading(false);
    setData(item);
    setSelectedButton(item);
  };

  return (
    <div className="weather-api">
      <ProjectPageHeader
        title={project.title}
        description={project.description}
        tech={project.tech}
      />

      <div className="weather-api__layout">
        <div className="weather-api__main">
          <p className="weather-api__controls-label">Search a city</p>

          <div className="weather-api__stats">
            <span className="weather-api__stat">
              Searches: <strong>{cityCount}</strong>
            </span>
            <span className="weather-api__stat">
              Limit: <strong>{API_COUNT_LIMIT}</strong>
            </span>
          </div>

          {cityCountLimt && (
            <p className="weather-api__limit-notice">
              Search limit reached — select a city from history.
            </p>
          )}

          {!cityCountLimt && <BasicSelect onChange={handleCityChange} />}

          {isLoading && <LinearProgress sx={progressSx} />}

          {error && !isLoading && (
            <div className="weather-api__error">{error}</div>
          )}

          {data && !isLoading && typeof data === 'object' && (
            <div className="weather-api__result">
              <div className="weather-api__card">
                <div>
                  <h2 className="weather-api__city">{data.name}</h2>
                  <div className="weather-api__details">
                    <p>Temperature: <span>{formatTemperature(data.main.temp)}</span></p>
                    <p>Feels like: <span>{formatTemperature(data.main.feels_like)}</span></p>
                    <p>Conditions: <span>{data.weather[0].description}</span></p>
                    <p>Wind: <span>{data.wind.speed} km/h</span></p>
                    <p>Humidity: <span>{data.main.humidity}%</span></p>
                    <p>Sunrise: <span>{formatTime(data.sys.sunrise + (data.timezone || 0))}</span></p>
                    <p>Sunset: <span>{formatTime(data.sys.sunset + (data.timezone || 0))}</span></p>
                    <p>Country: <span>{data.sys.country}</span></p>
                  </div>
                </div>
                <div className="weather-api__icon">
                  <img
                    src={`https://openweathermap.org/img/wn/${data.weather[0].icon}@2x.png`}
                    alt={data.weather[0].description}
                  />
                </div>
              </div>

              <div className="weather-api__toggle">
                <button
                  type="button"
                  className="api-button"
                  onClick={() => setIsRawDataVisible((prev) => !prev)}
                >
                  {isRawDataVisible ? 'Hide raw JSON ▲' : 'Show raw JSON ▼'}
                </button>
              </div>

              {isRawDataVisible && (
                <div className="weather-api__raw">
                  <pre>{JSON.stringify(data, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="weather-api__sidebar">
          <h2 className="weather-api__sidebar-title">Search history</h2>
          <p className="weather-api__sidebar-hint">Click to revisit</p>

          {cityHistory.map((item) => (
            <List key={item.id || item.name} disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  className={`weather-api__history-item ${
                    selectedButton && selectedButton.name === item.name
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() => handleListClick(item)}
                >
                  <ListItemText
                    primary={item.name}
                    secondary={`${item.main.temp}°C`}
                    primaryTypographyProps={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.8125rem',
                    }}
                    secondaryTypographyProps={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.75rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          ))}
        </aside>
      </div>
    </div>
  );
}

export default ApiPage;
